import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import FuelAnnexSectorsValidator from "../validators/FuelAnnexSectorsValidator";
import FuelAnnexRouteList from "./FuelAnnexRouteList";
import FuelAnnexSurchargeList from "./FuelAnnexSurchargeList";

import { useFuelAnnexSectors, useAddFuelAnnexSector, useUpdateFuelAnnexSector, useDeleteFuelAnnexSector } from "../../../services/fuel";
import { 
  MaskedText, 
  DatePicker, 
  MaskedNumber,
} from "../../../controls";
import { defaultComponents } from '../../../controls/Material-Table-Components';

const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();

const FuelAnnexSectorList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(FuelAnnexSectorsValidator, true);

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });
  const validFrom = props.validFrom;
  const validTo = props.validTo;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddFuelAnnexSector = useAddFuelAnnexSector();
  const handleUpdateFuelAnnexSector = useUpdateFuelAnnexSector();
  const handleDeleteFuelAnnexSector = useDeleteFuelAnnexSector();

  const queryClient = useQueryClient();
  const extraKeys = [props.fuelAnnexUuid];

  const fuelAnnexSectors = useFuelAnnexSectors({tableRef, queryClient, extraKeys});

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": ".",
      "d": "[1-7]",
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        title: "Fuel Annex Uuid",
        field: "fuelAnnexUuid",
        emptyValue: null,
        initialEditValue: null,
        hidden: props.fuelAnnexUuid !== ""?true:false,
        editComponent: (props) => (
          <MaskedText
            mask={"i".repeat(36)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(30),
        title: "#",
        field: "sequence",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(2)}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.sequence}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Sector Type",
        field: "sectorType",
        initialEditValue: "",
        lookup: {
          KG: "Per Kilo",
          BASE: "Base",
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Sector Name",
        field: "sectorName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(60, undefined, undefined, undefined, {paddingRight: 12}),
        title: "Day",
        field: "day",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            mask={"d".repeat(1)}
            formatChars={formatChars}
            maskChar={""}
            value={props.value}
            decimalScale={0}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.day}
            decimalScale={0}
          />
        )
      },
      {
        ...baseColumn,
        title: "Commodity",
        field: "commodity",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Base Fuel Date",
        field: "baseFuelDate",
        type: "date",
        emptyValue: today,
        initialEditValue:validFrom,
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Base Rate Date",
        field: "baseRateDate",
        type: "date",
        emptyValue: today,
        initialEditValue:validFrom,
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Base Rate",
        field: "baseRate",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.baseRate}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(90),
        title: "Minimum Amount",
        field: "minimumAmount",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={5}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.minimumAmount}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Pivot Rate",
        field: "overPivotRate",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.overPivotRate}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Rounding",
        field: "roundingMethod",
        initialEditValue: "ROUND",
        lookup: {
          "ROUND": "Default",
          "CEIL": "Ceiling",
          "FLOOR": "Floor",
          "NEXT CENT": "Next Cent",
        },
      },      
      {
        ...baseColumn,
        ...ColumnWidth(70),
        title: "Usable Payload",
        field: "usablePayload",
        type: "numeric",
        emptyValue: null,
        initialEditValue: 100000,
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={0}
            fixedDecimalScale={false}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        rende1r: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.usablePayload}
            decimalScale={0}
            fixedDecimalScale={false}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12}),
        title: "Valid From",
        field: "validFrom",
        type: "date",
        emptyValue: today,
        initialEditValue:validFrom,
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120),
        title: "Valid To",
        field: "validTo",
        type: "date",
        emptyValue: today,
        initialEditValue:validTo,
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{ marginLeft: 40 }}> {/*style={{width: "100%", marginLeft: 20, marginRight: 20}}*/}
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 5,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Sectors"
            columns={getColumns({canEdit: state.canEdit})}
            detailPanel={
              [
                {
                  tooltip: "Show Routes",
                  icon: 'flight',
                  render: (rowData) => {
                    return (
                      <div >
                        <div style={{ backgroundColor: "#f2f2f2" }}>
                          <FuelAnnexRouteList
                            canEdit={state.canEdit}
                            fuelAnnexSectorUuid={rowData.fuelAnnexSectorUuid}
                          />
                        </div>
                      </div>
                    );
                  },
                },
                {
                  tooltip: "Show Surcharges",
                  icon: 'queue',
                  render: (rowData) => {
                    return (
                      <div>
                        <div style={{ backgroundColor: "#f2f2f2", marginRight: 20, marginLeft:20 }}>
                          <FuelAnnexSurchargeList
                            canEdit={state.canEdit}
                            fuelAnnexSectorUuid={rowData.fuelAnnexSectorUuid}
                          />
                        </div>
                      </div>
                    );
                  },
                },
              ]
            }
            data={fuelAnnexSectors.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                    handleAddFuelAnnexSector.mutate({record: {fuelAnnexUuid: props.fuelAnnexUuid, ...newData}, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                    handleUpdateFuelAnnexSector.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    handleDeleteFuelAnnexSector.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
            }}
        />
      </div>
  );

}

export default FuelAnnexSectorList;
